'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { Header } from '@/app/components/shared/Header';
import { toast } from '@/app/components/ui/use-toast';
import { useGlobalModal, useIsScreen } from '@/app/hooks';
import type { StudioV1ClipProject } from '@/app/types';
import { useRouter } from '@/i18n/routing';
import { DesktopOnlyNotice } from '../DesktopOnlyNotice';
import { useCreateClipProject, useDeleteClipProject, useListClipProjects, useRenameClipProject } from './apis';
import { CreateCard } from './components/CreateCard';
import { ProjectCard } from './components/ProjectCard';

const SCROLL_CONTAINER_ID = 'video-edit-list-scroll';

type FetchStatus = 'idle' | 'loading' | 'success' | 'error';

export const VideoEditList = () => {
  const { isSmScreen, isMdScreen, matchList } = useIsScreen();

  if (matchList.length === 0) {
    return (
      <div className="h-full bg-black">
        <GlobalLoader />
      </div>
    );
  }

  if (isSmScreen || isMdScreen) {
    return <DesktopOnlyNotice />;
  }

  return <VideoEditListContent />;
};

const VideoEditListContent = () => {
  const t = useTranslations('videoEditor.list');
  const tRoot = useTranslations();
  const router = useRouter();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const [projects, setProjects] = useState<StudioV1ClipProject[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState<FetchStatus>('idle');
  const pageRef = useRef(0);

  const { fetchListAsync, pageSize } = useListClipProjects();
  const { loading: creating, createAsync } = useCreateClipProject();
  const { deleteAsync } = useDeleteClipProject();
  const { renameAsync } = useRenameClipProject();

  const loadPage = useCallback(
    async (page: number, mode: 'initial' | 'append') => {
      if (mode === 'initial') setStatus('loading');
      try {
        const { data, error } = await fetchListAsync(page);
        if (error || !data) {
          if (mode === 'initial') setStatus('error');
          else toast({ type: 'error', message: error?.message || t('loadFailed') });
          return;
        }
        const list = data.projects ?? [];
        setProjects((prev) => (mode === 'initial' ? list : [...prev, ...list]));
        pageRef.current = page + 1;
        setHasMore(list.length >= pageSize);
        if (mode === 'initial') setStatus('success');
      } catch {
        if (mode === 'initial') setStatus('error');
        else toast({ type: 'error', message: t('loadFailed') });
      }
    },
    [fetchListAsync, pageSize, t],
  );

  useEffect(() => {
    pageRef.current = 0;
    void loadPage(0, 'initial');
  }, [loadPage]);

  const handleLoadMore = useCallback(() => {
    if (status !== 'success') return;
    void loadPage(pageRef.current, 'append');
  }, [loadPage, status]);

  const handleCreate = async () => {
    if (creating) return;
    const { data, error } = await createAsync(t('defaultProjectName'));
    if (error || !data?.project?.id) {
      toast({ type: 'error', message: error?.message || tRoot('popup.toast.fetchError') });
      return;
    }
    router.push(`/create/ai-video-cutter/${data.project.id}`);
  };

  const handleOpen = useCallback(
    (id: string) => {
      router.push(`/create/ai-video-cutter/${id}`);
    },
    [router],
  );

  const handleRename = useCallback(
    async (id: string, nextName: string) => {
      const { error } = await renameAsync(id, nextName);
      if (error) {
        if (error.reason === 'Conflict') {
          toast({ type: 'error', message: tRoot('agentProject.warning.nameConflict') });
        } else {
          toast({ type: 'error', message: error.message || tRoot('popup.toast.fetchError') });
        }
        return false;
      }
      setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, name: nextName } : p)));
      toast({ type: 'success', message: tRoot('agentProject.project.renameSuccess') });
      return true;
    },
    [renameAsync, tRoot],
  );

  const handleRequestDelete = useCallback(
    (project: StudioV1ClipProject) => {
      const id = project.id;
      if (!id) return;
      openConfirmDialog({
        title: t('deleteConfirm.title'),
        content: t('deleteConfirm.content'),
        primaryButtonText: t('deleteConfirm.primaryButton'),
        secondaryButtonText: t('deleteConfirm.secondaryButton'),
        onPrimaryAction: async () => {
          const { error } = await deleteAsync(id);
          if (error) {
            toast({ type: 'error', message: error.message || t('deleteError') });
            return;
          }
          setProjects((prev) => prev.filter((p) => p.id !== id));
          toast({ type: 'success', message: t('deleteSuccess') });
        },
      });
    },
    [deleteAsync, openConfirmDialog, t],
  );

  const renderGrid = (items: StudioV1ClipProject[]) => (
    <div className="grid grid-cols-[repeat(auto-fill,240px)] gap-5">
      <CreateCard loading={creating} onClick={handleCreate} />
      {items.map((project) => (
        <ProjectCard
          key={project.id}
          project={project}
          onOpen={handleOpen}
          onRequestDelete={handleRequestDelete}
          onRename={handleRename}
        />
      ))}
    </div>
  );

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <div className="grid grid-cols-[repeat(auto-fill,240px)] gap-5">
          <CreateCard loading={creating} onClick={handleCreate} />
          {Array.from({ length: 9 }).map((_, idx) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: 占位骨架，无业务 id
            <div key={idx} className="h-[136px] w-[240px] animate-pulse rounded-2xl bg-system-white04" />
          ))}
        </div>
      );
    }

    if (projects.length === 0) {
      return (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-[repeat(auto-fill,240px)] gap-5">
            <CreateCard loading={creating} onClick={handleCreate} />
          </div>
          {status === 'error' && <div className="text-sm text-system-white64">{t('errorTitle')}</div>}
        </div>
      );
    }

    return (
      <InfiniteScroll
        dataLength={projects.length}
        next={handleLoadMore}
        hasMore={hasMore}
        loader={
          <div className="flex w-full items-center justify-center py-4 text-sm text-system-white64">
            <BaseLineLoading2 className="mr-2 size-4 animate-spin" />
            {t('loadMore')}
          </div>
        }
        endMessage={
          projects.length > pageSize ? (
            <div className="py-4 text-center text-sm text-system-white48">{t('noMore')}</div>
          ) : null
        }
        scrollableTarget={SCROLL_CONTAINER_ID}
        scrollThreshold={0.9}
      >
        {renderGrid(projects)}
      </InfiniteScroll>
    );
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header className="shrink-0" />
      <div id={SCROLL_CONTAINER_ID} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1368px] px-11 pt-6 pb-12">
          <div className="flex flex-col gap-3">
            <h1 className="font-semibold text-3xl text-white">{t('title')}</h1>
            <p className="max-w-[424px] text-sm text-system-white48">{t('subtitle')}</p>
          </div>

          <div className="mt-10 flex items-center justify-between">
            <h2 className="font-medium text-system-white text-xl">{t('projectsTitle')}</h2>
          </div>

          <div className="mt-4">{renderBody()}</div>
        </div>
      </div>
    </div>
  );
};
